import { ChartSvg, ChartTitle } from "@/components/tool-kit/charts/chart-svg";
import { px } from "@/components/tool-kit/charts/marks";
import { TextLines } from "@/components/tool-kit/diagrams/svg-parts";
import { textColorOn } from "@/lib/tool-kit/charts/color";
import { LINE_HEIGHT } from "@/lib/tool-kit/charts/layout";
import { tint } from "@/lib/tool-kit/diagrams/color";
import { titledFrame } from "@/lib/tool-kit/diagrams-more/frame";
import {
  DETAIL_FONT,
  NAME_FONT,
  type OrgChartLayout,
  type PlacedOrgNode,
} from "@/lib/tool-kit/diagrams-more/org-chart";
import { CHART_INK } from "@/lib/tool-kit/palette";

/*
 * An org chart from `layoutOrgChart`. The top of each tree is a dark box; the units one
 * level down are filled with their branch colour, and everything below is a pale box
 * outlined in it, so each department reads as one colour. Connectors are drawn first.
 */

function boxColors(entry: PlacedOrgNode, color: string) {
  const depth = entry.node.depth;
  if (depth === 0) {
    return { fill: CHART_INK.text, stroke: CHART_INK.text, text: "#ffffff" };
  }
  if (depth === 1) {
    return { fill: color, stroke: color, text: textColorOn(color) };
  }
  return { fill: tint(color, 0.9), stroke: color, text: CHART_INK.text };
}

export function OrgChartDiagram({
  id,
  layout,
  colorOf,
  title,
  label,
  description,
}: {
  id: string;
  layout: OrgChartLayout;
  /** The colour of a node's branch. */
  colorOf: (nodeId: number) => string;
  title: string;
  label: string;
  description: string;
}) {
  const frame = titledFrame(layout.width, layout.height, title);
  const nameHeight = NAME_FONT * LINE_HEIGHT;
  const detailHeight = DETAIL_FONT * LINE_HEIGHT;
  return (
    <ChartSvg
      description={description}
      height={frame.height}
      id={id}
      label={label}
      width={frame.width}
    >
      <ChartTitle title={frame.title} />
      <g transform={`translate(${px(frame.dx)} ${px(frame.dy)})`}>
        {layout.connectors.map((points) => (
          <polyline
            fill="none"
            key={points.map(([x, y]) => `${px(x)},${px(y)}`).join(" ")}
            points={points.map(([x, y]) => `${px(x)},${px(y)}`).join(" ")}
            stroke={CHART_INK.muted}
            strokeLinejoin="round"
            strokeWidth={1.5}
          />
        ))}
        {layout.nodes.map((entry) => {
          const colors = boxColors(entry, colorOf(entry.node.id));
          const textHeight =
            entry.nameLines.length * nameHeight +
            entry.detailLines.length * detailHeight;
          const top = entry.rect.y + (entry.rect.height - textHeight) / 2;
          const centre = entry.rect.x + entry.rect.width / 2;
          return (
            <g key={`node-${entry.node.id}`}>
              <rect
                fill={colors.fill}
                height={px(entry.rect.height)}
                rx={6}
                stroke={colors.stroke}
                strokeWidth={1.75}
                width={px(entry.rect.width)}
                x={px(entry.rect.x)}
                y={px(entry.rect.y)}
              />
              <TextLines
                bold
                fill={colors.text}
                fontSize={NAME_FONT}
                lineHeight={nameHeight}
                lines={entry.nameLines}
                top={top}
                x={centre}
              />
              <TextLines
                fill={colors.text}
                fontSize={DETAIL_FONT}
                lineHeight={detailHeight}
                lines={entry.detailLines}
                top={top + entry.nameLines.length * nameHeight}
                x={centre}
              />
            </g>
          );
        })}
      </g>
    </ChartSvg>
  );
}
